// ไฟล์: frontend/src/pages/ResourcesPage.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./ResourcesPage.css";

const ResourcesPage = () => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: "",
    subject: "",
    gradeLevel: "",
    fileType: "",
    searchTerm: "",
  });
  const [sortOption, setSortOption] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  // ดึงข้อมูลสื่อการสอน หมวดหมู่ วิชา และระดับชั้น
  useEffect(() => {
    const fetchData = async () => {
      try {
        // ดึงข้อมูลพร้อมกัน
        const [resourcesRes, categoriesRes, subjectsRes, gradeLevelsRes] =
          await Promise.all([
            axios.get("/resources"),
            axios.get("/categories"),
            axios.get("/subjects"),
            axios.get("/grade-levels"),
          ]);

        setResources(resourcesRes.data.resources);
        setCategories(categoriesRes.data.categories);
        setSubjects(subjectsRes.data.subjects);
        setGradeLevels(gradeLevelsRes.data.gradeLevels);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // จัดการการเปลี่ยนแปลงของตัวกรอง
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value,
    });
  };

  // จัดการการเปลี่ยนแปลงของการเรียงลำดับ
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  // จัดการการรีเซ็ตตัวกรอง
  const handleResetFilters = () => {
    setFilters({
      category: "",
      subject: "",
      gradeLevel: "",
      fileType: "",
      searchTerm: "",
    });
  };

  // กรองและเรียงลำดับสื่อการสอน
  const filteredAndSortedResources = () => {
    // กรองสื่อการสอน
    let result = resources.filter((resource) => {
      // กรองตามหมวดหมู่
      if (
        filters.category &&
        resource.category_id.toString() !== filters.category
      ) {
        return false;
      }

      // กรองตามวิชา
      if (
        filters.subject &&
        resource.subject_id.toString() !== filters.subject
      ) {
        return false;
      }

      // กรองตามระดับชั้น
      if (
        filters.gradeLevel &&
        resource.grade_level_id.toString() !== filters.gradeLevel
      ) {
        return false;
      }

      // กรองตามประเภทไฟล์
      if (filters.fileType && resource.file_type !== filters.fileType) {
        return false;
      }

      // กรองตามคำค้นหา
      if (filters.searchTerm) {
        const searchTerm = filters.searchTerm.toLowerCase();
        return (
          resource.title.toLowerCase().includes(searchTerm) ||
          resource.description.toLowerCase().includes(searchTerm) ||
          resource.author_name.toLowerCase().includes(searchTerm) ||
          resource.category_name.toLowerCase().includes(searchTerm) ||
          resource.subject_name.toLowerCase().includes(searchTerm) ||
          resource.grade_level_name.toLowerCase().includes(searchTerm)
        );
      }

      return true;
    });

    // เรียงลำดับสื่อการสอน
    switch (sortOption) {
      case "newest":
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case "oldest":
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case "popular":
        result.sort((a, b) => b.view_count - a.view_count);
        break;
      case "downloads":
        result.sort((a, b) => b.download_count - a.download_count);
        break;
      case "rating":
        result.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0));
        break;
      default:
        break;
    }

    return result;
  };

  // แสดงสถานะการโหลด
  if (loading) {
    return (
      <div className="loading-container">
        {/* <div className="loading-spinner">กำลังโหลดข้อมูล...</div> */}
      </div>
    );
  }

  // คำนวณสื่อการสอนที่กรองและเรียงลำดับแล้ว
  const displayedResources = filteredAndSortedResources();

  return (
    <div className="resources-page">
      <div className="resources-header">
        <div className="container">
          <h1>คลังสื่อการสอน</h1>
          <p>
            รวบรวมสื่อการสอนคุณภาพจากครูทั่วประเทศ สำหรับทุกวิชาและทุกระดับชั้น
          </p>
        </div>
      </div>

      <div className="container">
        <div className="resources-actions">
          <div className="resources-search">
            <input
              type="text"
              name="searchTerm"
              placeholder="ค้นหาสื่อการสอน..."
              value={filters.searchTerm}
              onChange={handleFilterChange}
              className="form-control"
            />
            <button
              className="btn btn-primary"
              onClick={() => setShowFilters(!showFilters)}
            >
              <i className="fas fa-filter"></i> ตัวกรอง
            </button>
          </div>

          <div className="resources-sort">
            <label htmlFor="sortOption">เรียงตาม:</label>
            <select
              id="sortOption"
              value={sortOption}
              onChange={handleSortChange}
              className="form-select"
            >
              <option value="newest">ล่าสุด</option>
              <option value="oldest">เก่าสุด</option>
              <option value="popular">ยอดนิยม</option>
              <option value="downloads">ดาวน์โหลดมากสุด</option>
              <option value="rating">คะแนนสูงสุด</option>
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="resources-filters">
            <div className="filters-row">
              <div className="filter-group">
                <label htmlFor="category">หมวดหมู่:</label>
                <select
                  id="category"
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="">ทั้งหมด</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="subject">วิชา:</label>
                <select
                  id="subject"
                  name="subject"
                  value={filters.subject}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="">ทั้งหมด</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="gradeLevel">ระดับชั้น:</label>
                <select
                  id="gradeLevel"
                  name="gradeLevel"
                  value={filters.gradeLevel}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="">ทั้งหมด</option>
                  {gradeLevels.map((gradeLevel) => (
                    <option key={gradeLevel.id} value={gradeLevel.id}>
                      {gradeLevel.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label htmlFor="fileType">ประเภทไฟล์:</label>
                <select
                  id="fileType"
                  name="fileType"
                  value={filters.fileType}
                  onChange={handleFilterChange}
                  className="form-select"
                >
                  <option value="">ทั้งหมด</option>
                  <option value="pdf">PDF</option>
                  <option value="ppt">PowerPoint</option>
                  <option value="video">วิดีโอ</option>
                  <option value="link">ลิงก์</option>
                  <option value="drive">Google Drive</option>
                  <option value="other">อื่นๆ</option>
                </select>
              </div>
            </div>

            <div className="filters-actions">
              <span className="results-count">
                พบ {displayedResources.length} รายการ
              </span>
              <button className="btn btn-light" onClick={handleResetFilters}>
                <i className="fas fa-undo"></i> รีเซ็ตตัวกรอง
              </button>
            </div>
          </div>
        )}

        {displayedResources.length === 0 ? (
          <div className="no-results">
            <div className="no-results-icon">
              <i className="fas fa-search"></i>
            </div>
            <h3>ไม่พบสื่อการสอนที่ตรงกับเงื่อนไข</h3>
            <p>ลองเปลี่ยนตัวกรองหรือคำค้นหาของคุณ</p>
            <button className="btn btn-primary" onClick={handleResetFilters}>
              <i className="fas fa-undo"></i> รีเซ็ตตัวกรอง
            </button>
          </div>
        ) : (
          <div className="resources-grid">
            {displayedResources.map((resource) => (
              <div className="resource-card" key={resource.id}>
                <div className="resource-thumbnail">
                  <img
                    src={
                      resource.thumbnail
                        ? `${process.env.REACT_APP_API_URL}/uploads/${resource.thumbnail}`
                        : "/assets/default-resource.jpg"
                    }
                    alt={resource.title}
                  />
                  <div className="resource-type">
                    {resource.file_type === "pdf" && (
                      <i className="fas fa-file-pdf"></i>
                    )}
                    {resource.file_type === "ppt" && (
                      <i className="fas fa-file-powerpoint"></i>
                    )}
                    {resource.file_type === "video" && (
                      <i className="fas fa-video"></i>
                    )}
                    {resource.file_type === "link" && (
                      <i className="fas fa-link"></i>
                    )}
                    {resource.file_type === "drive" && (
                      <i className="fab fa-google-drive"></i>
                    )}
                    {resource.file_type === "other" && (
                      <i className="fas fa-file"></i>
                    )}
                    {resource.file_type}
                  </div>
                </div>

                <div className="resource-content">
                  <h3 className="resource-title">
                    <Link to={`/resources/${resource.id}`}>
                      {resource.title}
                    </Link>
                  </h3>

                  <div className="resource-tags">
                    <span className="resource-tag">
                      <i className="fas fa-folder"></i> {resource.category_name}
                    </span>
                    <span className="resource-tag">
                      <i className="fas fa-book"></i> {resource.subject_name}
                    </span>
                    <span className="resource-tag">
                      <i className="fas fa-graduation-cap"></i>{" "}
                      {resource.grade_level_name}
                    </span>
                  </div>

                  <div className="resource-description">
                    {resource.description?.length > 120
                      ? `${resource.description.substring(0, 120)}...`
                      : resource.description}
                  </div>

                  <div className="resource-meta">
                    <div className="resource-author">
                      <img
                        src={`https://ui-avatars.com/api/?name=${resource.author_name}&background=random`}
                        alt={resource.author_name}
                      />
                      <span>{resource.author_name}</span>
                    </div>

                    <div className="resource-stats">
                      <div className="resource-views">
                        <i className="fas fa-eye"></i> {resource.view_count}
                      </div>
                      <div className="resource-downloads">
                        <i className="fas fa-download"></i>{" "}
                        {resource.download_count}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="resource-footer">
                  <div className="resource-rating">
                    <div className="rating">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`fas fa-star ${
                            star <= Math.round(resource.avg_rating || 0)
                              ? "filled"
                              : ""
                          }`}
                        ></i>
                      ))}
                      <span className="rating-value">
                        {resource.avg_rating !== undefined &&
                        resource.avg_rating !== null &&
                        !isNaN(Number(resource.avg_rating))
                          ? Number(resource.avg_rating).toFixed(1)
                          : "0.0"}
                      </span>
                    </div>
                  </div>

                  <Link
                    to={`/resources/${resource.id}`}
                    className="btn btn-sm btn-primary"
                  >
                    <i className="fas fa-info-circle"></i> รายละเอียด
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="resources-cta">
          <div className="cta-content">
            <h2>มีสื่อการสอนดีๆ อยากแบ่งปัน?</h2>
            <p>แบ่งปันสื่อการสอนของคุณให้กับเพื่อนครูทั่วประเทศ</p>
            <Link to="/upload-resource" className="btn btn-primary btn-lg">
              <i className="fas fa-upload"></i> อัปโหลดสื่อการสอน
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcesPage;
